import React from 'react';
import { State } from 'lib/enums';
import { TextField } from 'lib/components/TextField';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import CheckboxGroup from 'lib/components/Checkbox/CheckboxGroup';

export const textInputClasses =
  'form-input block w-full py-2 px-3 border border-gray-400 placeholder-gray-700 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5';
export const checkboxInputClasses =
  'form-checkbox cursor-pointer h-4 w-4 transition duration-150 ease-in-out text-gray-700 mr-3';

export const newPlaceholders = {
  phoneNumber: 'Enter phone number',
  address1: `Enter address 1`,
  address2: `Enter address 2`,
  city: 'Enter city',
  zip: 'Enter zip code',
  state: 'Select state'
};

export const newLabels = {
  phoneNumber: 'Phone number',
  address1: 'Address 1',
  address2: 'Address 2',
  city: 'City',
  zip: 'Zip code',
  state: 'State'
};

type RecipientEmailFormProps = {
  email: string;
  setEmail: (newVal: string) => void;
};
export function RecipientEmailForm({
  email,
  setEmail
}: RecipientEmailFormProps) {
  return (
    <div id="form-recipient-email" className="text-column-gray-500">
      <div className="w-full">
        <TextField
          id="field-email"
          type="email"
          labelText="Email address"
          placeholder="Email address *"
          required
          value={email}
          onChange={email => setEmail(email)}
        />
      </div>
    </div>
  );
}

type RecipientMailingFormProps = {
  zip: string;
  setZip: (newVal: string) => void;
  addressLine1: string;
  setAddressLine1: (newVal: string) => void;
  addressLine2: string;
  setAddressLine2: (newVal: string) => void;
  city: string;
  setCity: (newVal: string) => void;
  state: string;
  setState: (newVal: string) => void;
};
export function RecipientMailingForm({
  zip,
  setZip,
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  city,
  setCity,
  state,
  setState
}: RecipientMailingFormProps) {
  return (
    <div className="md:grid grid-cols-2 space-y-3 gap-x-3">
      <div className="col-span-2">
        <TextField
          id="field-address-line-1"
          type="text"
          placeholder={newPlaceholders.address1}
          value={addressLine1}
          required
          labelText={newLabels.address1}
          onChange={addressLine1 => setAddressLine1(addressLine1)}
        />
      </div>
      <div className="col-span-2">
        <TextField
          id="field-address-line-2"
          type="text"
          placeholder={newPlaceholders.address2}
          labelText={newLabels.address2}
          value={addressLine2}
          onChange={addressLine2 => setAddressLine2(addressLine2)}
        />
      </div>
      <div className="col-span-2">
        <TextField
          id="field-city"
          type="text"
          placeholder={newPlaceholders.city}
          labelText={newLabels.city}
          value={city}
          required
          onChange={city => setCity(city)}
        />
      </div>
      <ColumnSelect
        id="field-state"
        labelText={newLabels.state}
        placeholder={newPlaceholders.state}
        onChange={state => {
          const stateString = State.by_value(state)?.label;
          if (stateString) {
            setState(stateString);
          }
        }}
        allowUndefined
        options={State.items().map((state: Record<string, any>) => ({
          value: String(state.value),
          label: state.label
        }))}
        value={String(State.by_label(state)?.value) || ''}
      />
      <TextField
        id="field-zip"
        type="postal-code"
        placeholder={newPlaceholders.zip}
        labelText={newLabels.zip}
        value={zip}
        required
        onChange={zip => setZip(zip)}
      />
    </div>
  );
}

type EmailOrMailToggleProps = {
  sendToEmail: boolean;
  setSendToEmail: (newVal: boolean) => void;
};

export function EmailOrMailToggle({
  sendToEmail,
  setSendToEmail
}: EmailOrMailToggleProps) {
  return (
    <CheckboxGroup
      id={''}
      labelText="How would you like to send this invoice?"
      value={[
        {
          labelText: 'Send the invoice by email',
          id: 'email',
          checked: sendToEmail
        },
        {
          labelText: 'Send the invoice by mail',
          id: 'mail',
          checked: !sendToEmail
        }
      ]}
      type="radio"
      onChange={(value, changedOption) => {
        setSendToEmail(changedOption.id === 'email');
      }}
    />
  );
}

type EmailOrMailFormProps = {
  sendToEmail: boolean;
  email: string;
  setEmail: (newVal: string) => void;
  zip: string;
  setZip: (newVal: string) => void;
  addressLine1: string;
  setAddressLine1: (newVal: string) => void;
  addressLine2: string;
  setAddressLine2: (newVal: string) => void;
  city: string;
  setCity: (newVal: string) => void;
  state: string;
  setState: (newVal: string) => void;
  firstName: string;
  setFirstName: (newVal: string) => void;
  lastName: string;
  setLastName: (newVal: string) => void;
  organizationName: string;
  setOrganizationName: (newVal: string) => void;
};
export function EmailOrMailForm({
  sendToEmail,
  email,
  setEmail,
  zip,
  setZip,
  addressLine1,
  setAddressLine1,
  addressLine2,
  setAddressLine2,
  city,
  setCity,
  state,
  setState
}: EmailOrMailFormProps) {
  return (
    <div>
      {sendToEmail ? (
        <RecipientEmailForm email={email} setEmail={setEmail} />
      ) : (
        <RecipientMailingForm
          zip={zip}
          setZip={setZip}
          addressLine1={addressLine1}
          setAddressLine1={setAddressLine1}
          addressLine2={addressLine2}
          setAddressLine2={setAddressLine2}
          city={city}
          setCity={setCity}
          state={state}
          setState={setState}
        />
      )}
    </div>
  );
}
